// React import removed as it's not needed with new JSX transform
import { cn } from '../lib/utils';
import { Icon, type IconName } from './ui/icons';
import apiClient from '../lib/api-client';

interface NavItem {
  id: string;
  label: string;
  icon: IconName;
  isNew?: boolean;
  section?: never;
}

interface NavSection {
  section: string;
  id?: never;
  label?: never;
  icon?: never;
  isNew?: never;
}

type NavEntry = NavItem | NavSection;

// Unified navigation structure combining security and compliance
const NAV: NavEntry[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', isNew: true },
  { section: 'Security' },
  { id: 'security-events', label: 'Security Events', icon: 'shield' },
  { id: 'security-alerts', label: 'Security Alerts', icon: 'bell' },
  { id: 'alert-rules', label: 'Alert Rules', icon: 'settings' },
  { id: 'security-timeline', label: 'Timeline', icon: 'activity' },
  { section: 'Compliance' },
  { id: 'projects', label: 'Projects', icon: 'book' },
  { id: 'repositories', label: 'Repositories', icon: 'book' },
  { id: 'controls', label: 'Controls', icon: 'shield' },
  { id: 'frameworks', label: 'Frameworks', icon: 'shield' },
  { id: 'evidence', label: 'Evidence', icon: 'file' },
  { id: 'conformance', label: 'Conformance', icon: 'git-branch' },
  { id: 'risk-acceptances', label: 'Risk Acceptances', icon: 'alert-triangle' },
  { section: 'Automation' },
  { id: 'interpreters', label: 'Interpreters', icon: 'git-branch' },
  { id: 'normalizers', label: 'Normalizers', icon: 'package' },
  { id: 'tools', label: 'Connected Tools', icon: 'package' },
  { section: 'Insights' },
  { id: 'remediations', label: 'Remediations', icon: 'arrow-up-right' },
  { id: 'insights', label: 'Cross-Domain Insights', icon: 'trending-up', isNew: true },
  { id: 'activity', label: 'Activity Feed', icon: 'activity' },
  { section: 'Organization' },
  { id: 'tags', label: 'Tags', icon: 'tag' },
  { id: 'settings', label: 'Settings', icon: 'settings' },
];

interface NavItemProps {
  item: NavItem;
  active: boolean;
  onClick: () => void;
}

function NavItem({ item, active, onClick }: NavItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3.5 py-2.5 rounded-[12px] text-left transition',
        'font-display text-[14px] text-ink',
        active 
          ? 'bg-primary-100 font-semibold text-primary-700' 
          : 'hover:bg-gray-200 font-medium'
      )}
    >
      <Icon name={item.icon} size={16} stroke="currentColor" />
      <span className="flex-1 truncate">{item.label}</span>
      {item.isNew && (
        <span className="font-display text-[10px] font-bold uppercase tracking-wider px-1.5 py-[2px] rounded-[3px] bg-primary-500 text-white">
          New
        </span>
      )}
    </button>
  );
}

interface SidebarProps {
  route: string;
  navigate: (route: string) => void;
}

export default function Sidebar({ route, navigate }: SidebarProps) {
  const auth = apiClient.getAuth();

  const handleLogout = () => {
    apiClient.clearAuth();
    navigate('login');
  };

  return (
    <aside className="bg-gray-50 border-r border-gray-200 flex flex-col justify-between px-5 py-10 w-[266px] shrink-0 h-screen sticky top-0 overflow-y-auto">
      <div className="flex flex-col gap-10">
        {/* Logo */}
        <button
          type="button"
          onClick={() => navigate('dashboard')}
          className="flex items-center gap-2 px-1 text-left"
        >
          <div
            className="h-7 w-7 rounded-[6px] grid place-items-center"
            style={{ background: '#121111' }}
          >
            <span className="font-display font-extrabold text-white text-[14px] tracking-tight">
              C
            </span>
          </div>
          <span className="font-display font-bold text-ink text-[22px] tracking-tight">
            Cylift<span className="text-primary-500">.Dev</span>
          </span>
        </button>

        {/* Navigation */}
        <nav className="flex flex-col gap-0.5">
          {NAV.map((item, i) =>
            'section' in item ? (
              <div
                key={item.section || `nav-section-${i}`}
                className="px-3.5 pt-4 pb-1 font-display text-[10px] font-bold uppercase tracking-[0.12em] text-ink/40"
              >
                {item.section}
              </div>
            ) : (
              <NavItem
                key={item.id}
                item={item}
                active={route === item.id}
                onClick={() => navigate(item.id)}
              />
            )
          )}

          <div className="my-4 border-t border-gray-200" />

          {/* Support Links */}
          <button
            type="button"
            onClick={() => window.open('https://github.com/cylift/cylift/discussions', '_blank')}
            className="flex items-center justify-between px-3.5 py-1 rounded-[8px] hover:bg-gray-200 transition"
          >
            <span className="font-display text-[14px] font-medium text-ink">Support</span>
            <Icon name="external" size={14} stroke="rgba(18,17,17,0.4)" />
          </button>
          <button
            type="button"
            onClick={() => window.open('https://docs.cylift.dev', '_blank')}
            className="flex items-center justify-between px-3.5 py-1 rounded-[8px] hover:bg-gray-200 transition"
          >
            <span className="font-display text-[14px] font-medium text-ink">Documentation</span>
            <Icon name="external" size={14} stroke="rgba(18,17,17,0.4)" />
          </button>
        </nav>
      </div>

      {/* Bottom Section */}
      <div className="flex flex-col gap-6">
        {/* Upgrade Card */}
        <div className="rounded-[10px] bg-white p-5 shadow-[0_2px_3px_0_rgba(0,0,0,0.06)] border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <span className="font-display font-bold text-ink text-[16px]">Upgrade to</span>
            <span
              className="font-display font-bold text-ink text-[13px] px-1.5 py-[2px] rounded-[2px]"
              style={{ background: '#F1E9D7' }}
            >
              PRO
            </span>
          </div>
          <div className="font-display text-[12px] font-bold text-ink mb-1">
            Unified security & compliance
          </div>
          <div className="font-display text-[12px] font-bold text-ink mb-4">
            Advanced analytics & insights
          </div>
          <button
            type="button"
            onClick={() => navigate('settings')}
            className="w-full bg-ink text-white font-display font-bold text-[14px] py-2.5 rounded-[10px] hover:opacity-90 transition"
          >
            Upgrade
          </button>
        </div>

        <div className="border-t border-gray-200 -mx-1" />

        {/* User Account */}
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-3">
            <div
              className="h-8 w-8 rounded-[4px] grid place-items-center text-white font-display font-extrabold text-[14px]"
              style={{ background: 'linear-gradient(135deg,#FA500E,#7F56D9)' }}
            >
              {auth?.user.name.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex flex-col">
              <span className="font-display font-bold text-ink text-[14px]">
                {auth?.user.name || 'User'}
              </span>
              <span className="font-display text-[12px] text-ink/60 capitalize">
                {auth?.user.role || 'viewer'}
              </span>
            </div>
          </div>
          
          <button
            type="button"
            onClick={handleLogout}
            className="p-1 hover:bg-gray-200 rounded transition"
            title="Logout"
          >
            <Icon name="settings" size={16} stroke="#121111" />
          </button>
        </div>
      </div>
    </aside>
  );
}